import useTranscripts from 'api/hooks/useTranscripts';
import useUser from 'api/hooks/useUser';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import DeleteTranscriptModal from 'components/Dashboard/Transcripts/DeleteTranscriptModal';
import TranscriptsItem from 'components/Dashboard/Transcripts/TranscriptsItem';
import useTranscriptsSort from 'components/Dashboard/Transcripts/useTranscriptsSort';
import { SkeletonContent } from 'components/WritingAssistant/ui/MyContent';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useSearchParams } from 'react-router-dom';
import filterTranscriptsByCategory from 'components/Dashboard/Transcripts/filterTranscriptsByCategory';
import { store } from 'components/Store';

const statusGroup = (status) => {
  if (status === 'Processing') return 0;
  if (status === 'Emailed') return 1; // shows as "Ready"
  if (status === 'Pending') return 2; // Scheduled
  return 3; // NoEmailed, Error - show their actual status
};

const YourTranscripts = (props) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { userProfile } = state ?? {};

  const { openState, activeDebateState, transcriptsState, activeSectionState } = props;
  const { setIsOpen } = openState;
  const { setActiveDebate } = activeDebateState;
  const { transcripts, setTranscripts } = transcriptsState;
  const { activeSection } = activeSectionState;
  const { sortOrder } = useTranscriptsSort();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortedTranscripts = useMemo(() => {
    const filtered = filterTranscriptsByCategory(transcripts ?? [], activeSection);
    return [...filtered].sort((a, b) => {
      if (sortOrder === 'Status') {
        const groupDiff = statusGroup(a.status) - statusGroup(b.status);
        if (groupDiff !== 0) return groupDiff;
      }
      return new Date(b.createdAtUtc) - new Date(a.createdAtUtc);
    });
  }, [transcripts, sortOrder, activeSection]);

  const [heightContainer, containerRef] = useHeightContainer();
  const { getCurrentUser } = useUser();
  const { getListTranscripts } = useTranscripts();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const initialLoad = useRef(null);
  initialLoad.current = async (signal) => {
    try {
      if (Object.keys(userProfile)?.length === 0) {
        const currentUser = await getCurrentUser({ signal });
        if (currentUser) dispatch({ type: 'MODIFY_SECTION', parameter: 'userProfile', value: currentUser });
      }
      const data = await getListTranscripts({ signal });
      if (!!data) {
        setTranscripts(data);
        const sortParam = getUrlParam('sort');
        const next = new URLSearchParams(searchParams);
        if (!sortParam) {
          next.set('sort', 'status');
        }
        setSearchParams(next);
        setIsLoading(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const controller = new AbortController();
    initialLoad.current(controller.signal);
    return () => {
      controller.abort('Transcripts list cancelled by the user.');
    };
  }, []);

  return (
    <>
      <div ref={containerRef}>
        <CustomScrollbar
          className={`main-content-scrollbar`}
          style={{ height: `${heightContainer}px` }}
          maximalThumbYSize={100}
        >
          <div className='px-5 specific-top-padding-28'>
            {isLoading ? (
              <SkeletonContent />
            ) : (
              <>
                {sortedTranscripts?.length > 0 ? (
                  <>
                    <ul className='policy-list px-0'>
                      {sortedTranscripts?.map((item) => {
                        const { id } = item;
                        return (
                          <TranscriptsItem
                            key={`item-transcript-${id}`}
                            item={item}
                            setActiveDebate={setActiveDebate}
                            setIsOpen={setIsOpen}
                            setDeleteItem={setDeleteItem}
                            setShowDeleteModal={setShowDeleteModal}
                          />
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <p className='mb-0 light-60-text text-center mt-5 pt-5'>
                    Your team has not created or scheduled any transcripts yet
                  </p>
                )}
              </>
            )}
          </div>
        </CustomScrollbar>
      </div>
      <DeleteTranscriptModal
        isOpen={showDeleteModal}
        setIsOpen={setShowDeleteModal}
        deleteItemState={{ deleteItem, setDeleteItem }}
        setTranscripts={setTranscripts}
      />
    </>
  );
};

export default YourTranscripts;
